import React, { type ReactNode } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import { User, Settings, Info, X } from "@xsolla/xui-icons";

// --- Types ---

export type EntityType = "item" | "product" | "account";

export type StatusType = "sale" | "maintenance" | "inactive";

export type SubentityType =
  | "skin"
  | "in-game-item"
  | "currency"
  | "bundle"
  | "lootbox"
  | "game-key"
  | "discount"
  | "coupon"
  | "game"
  | "sdk"
  | "plugin"
  | "api"
  | "service"
  | "tool"
  | "webshop"
  | "app"
  | "launcher"
  | "library"
  | "engine"
  | "user"
  | "developer"
  | "influencer"
  | "publisher"
  | "settings";

export interface TagLabelProps extends ThemeOverrideProps {
  /** Main entity type */
  entityType?: EntityType;
  /** Detailed sub-category */
  subentityType?: SubentityType;
  /** Custom icon for entity/subentity if needed */
  icon?: ReactNode;
  /** ID series (e.g. "XLA") */
  series?: string;
  /** ID number (e.g. "-001-001") */
  number?: string;
  /** Item label/name */
  label?: string;
  /** Rarity icon or indicator */
  rarity?: ReactNode;
  /** Status indicator */
  status?: StatusType;
  /** Whether to show the compact version */
  compact?: boolean;
  /** Custom content slot */
  children?: ReactNode;
  /** Custom styles */
  style?: any;
  testID?: string;
}

// --- Internal Segments ---

const SEGMENT_HEIGHT = 24;

const TagLabelEntitySegment: React.FC<{
  type: EntityType;
  compact?: boolean;
  icon?: ReactNode;
  theme: any;
}> = ({ type, compact, icon, theme }) => {
  const tagLabel = theme?.colors?.control?.tagLabel;
  const colors = tagLabel?.[type];

  if (!colors) return null;

  // Generic icon placeholders for now based on Figma intent
  const renderIcon = () => {
    if (icon) return icon;
    switch (type) {
      case "item":
        return (
          <Box
            style={{ width: 12, height: 12, backgroundColor: colors.text }}
          />
        ); // Cube placeholder
      case "product":
        return (
          <Box
            style={{ width: 12, height: 12, backgroundColor: colors.text }}
          />
        ); // Chip placeholder
      case "account":
        return <User size="100%" />;
    }
  };

  if (compact) {
    return (
      <Box
        height={SEGMENT_HEIGHT}
        width={SEGMENT_HEIGHT}
        alignItems="center"
        justifyContent="center"
      >
        <Icon size={16} color={colors.textCompact || colors.text}>
          {renderIcon()}
        </Icon>
      </Box>
    );
  }

  return (
    <Box
      height={SEGMENT_HEIGHT}
      backgroundColor={colors.bg}
      paddingLeft={2}
      paddingRight={4}
      flexDirection="row"
      alignItems="center"
      gap={2}
    >
      <Icon size={20} color={colors.text}>
        {renderIcon()}
      </Icon>
      <Text
        color={colors.text}
        fontSize={14}
        fontWeight="500"
        whiteSpace="nowrap"
      >
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Text>
    </Box>
  );
};

const TagLabelSubentitySegment: React.FC<{
  type: SubentityType;
  theme: any;
}> = ({ type, theme }) => {
  const tagLabel = theme?.colors?.control?.tagLabel;
  const colors = tagLabel?.subentity;

  if (!colors || !type || typeof type !== "string") return null;

  // Icon mapping based on Figma
  const renderIcon = () => {
    switch (type) {
      case "skin":
      case "in-game-item":
        return (
          <Box
            style={{ width: 12, height: 12, backgroundColor: colors.text }}
          />
        ); // Pencil placeholder
      case "game":
        return (
          <Box
            style={{ width: 12, height: 12, backgroundColor: colors.text }}
          />
        ); // Controller placeholder
      case "user":
        return <User size="100%" />;
      case "settings":
        return <Settings size="100%" />;
      // ... more icons as needed
      default:
        return <Info size="100%" />;
    }
  };

  const label = type
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <Box
      height={SEGMENT_HEIGHT}
      backgroundColor={colors.bg}
      paddingLeft={2}
      paddingRight={4}
      flexDirection="row"
      alignItems="center"
      gap={2}
    >
      <Icon size={20} color={colors.text}>
        {renderIcon()}
      </Icon>
      <Text
        color={colors.text}
        fontSize={14}
        fontWeight="500"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </Box>
  );
};

const TagLabelIdSegment: React.FC<{
  series?: string;
  number?: string;
  compact?: boolean;
  theme: any;
}> = ({ series, number, compact, theme }) => {
  const tagLabel = theme?.colors?.control?.tagLabel;
  const colors = tagLabel?.id;

  if (!colors) return null;

  if (compact) {
    return (
      <Box
        height={SEGMENT_HEIGHT}
        paddingLeft={4}
        paddingRight={6}
        flexDirection="row"
        alignItems="center"
      >
        {series && (
          <Text color={colors.textCompact} fontSize={14} fontWeight="400">
            {series}
          </Text>
        )}
        {number && (
          <Text color={colors.textCompact} fontSize={14} fontWeight="400">
            {number}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Box
      height={SEGMENT_HEIGHT}
      backgroundColor={colors.bg}
      paddingLeft={4}
      paddingRight={6}
      flexDirection="row"
      alignItems="center"
      gap={2}
    >
      {series && (
        <Box
          backgroundColor={colors.bgSeries}
          height={16}
          paddingHorizontal={2}
          alignItems="center"
          justifyContent="center"
        >
          <Text color={colors.textSeries} fontSize={14} fontWeight="500">
            {series}
          </Text>
        </Box>
      )}
      {number && (
        <Text color={colors.textNumber} fontSize={14} fontWeight="500">
          {number}
        </Text>
      )}
    </Box>
  );
};

const TagLabelNameSegment: React.FC<{ label: string; theme: any }> = ({
  label,
  theme,
}) => {
  const tagLabel = theme?.colors?.control?.tagLabel;
  const colors = tagLabel?.name;

  if (!colors) return null;

  return (
    <Box
      height={SEGMENT_HEIGHT}
      backgroundColor={colors.bg}
      paddingHorizontal={4}
      justifyContent="center"
    >
      <Text
        color={colors.text}
        fontSize={14}
        fontWeight="400"
        whiteSpace="nowrap"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Text>
    </Box>
  );
};

const TagLabelRaritySegment: React.FC<{ rarity: ReactNode; theme: any }> = ({
  rarity,
  theme,
}) => {
  const tagLabel = theme?.colors?.control?.tagLabel;
  const colors = tagLabel?.rarity;

  if (!colors) return null;

  return (
    <Box
      height={SEGMENT_HEIGHT}
      width={SEGMENT_HEIGHT}
      backgroundColor={colors.bg}
      paddingHorizontal={6}
      alignItems="center"
      justifyContent="center"
    >
      <Icon size={18} color={colors.text}>
        {rarity || <Info size="100%" />}
      </Icon>
    </Box>
  );
};

const TagLabelStatusSegment: React.FC<{ type: StatusType; theme: any }> = ({
  type,
  theme,
}) => {
  let bg;
  let icon;

  if (!theme?.colors?.background) return null;

  switch (type) {
    case "sale":
      bg = theme.colors.background.warning?.primary;
      icon = <Info size="100%" />; // Info placeholder
      break;
    case "maintenance":
      bg = theme.colors.background.success?.primary;
      icon = <Settings size="100%" />;
      break;
    case "inactive":
      bg = theme.colors.background.alert?.primary;
      icon = <X size="100%" />;
      break;
  }

  return (
    <Box
      height={SEGMENT_HEIGHT}
      width={SEGMENT_HEIGHT}
      backgroundColor={bg}
      paddingHorizontal={2}
      alignItems="center"
      justifyContent="center"
    >
      <Icon size={20} color="black">
        {icon}
      </Icon>
    </Box>
  );
};

const TagLabelCustomSlot: React.FC<{ children: ReactNode; theme: any }> = ({
  children,
  theme,
}) => {
  if (!theme?.colors?.background) return null;

  return (
    <Box
      height={SEGMENT_HEIGHT}
      backgroundColor={theme.colors.background.secondary}
      paddingHorizontal={6}
      justifyContent="center"
    >
      {typeof children === "string" ? (
        <Text
          color={theme?.colors?.content?.primary}
          fontSize={14}
          fontWeight="500"
        >
          {children}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
};

// --- Main Component ---

export const TagLabel: React.FC<TagLabelProps> = ({
  entityType,
  subentityType,
  icon,
  series,
  number,
  label,
  rarity,
  status,
  compact = false,
  children,
  style,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  return (
    <Box
      testID={testID}
      flexDirection="row"
      alignItems="center"
      style={{
        overflow: "hidden",
        borderRadius: theme.shape.tagLabel.borderRadius,
        ...style,
      }}
    >
      {entityType && (
        <TagLabelEntitySegment
          type={entityType}
          compact={compact}
          icon={icon}
          theme={theme}
        />
      )}

      {!compact && subentityType && (
        <TagLabelSubentitySegment type={subentityType} theme={theme} />
      )}

      {(series || number) && (
        <TagLabelIdSegment
          series={series}
          number={number}
          compact={compact}
          theme={theme}
        />
      )}

      {!compact && label && <TagLabelNameSegment label={label} theme={theme} />}

      {!compact && rarity && (
        <TagLabelRaritySegment rarity={rarity} theme={theme} />
      )}

      {!compact && status && (
        <TagLabelStatusSegment type={status} theme={theme} />
      )}

      {!compact && children && (
        <TagLabelCustomSlot theme={theme}>{children}</TagLabelCustomSlot>
      )}
    </Box>
  );
};
